import { useEffect, useRef, type JSX } from 'react';
import { createPortal } from 'react-dom';
import type { ContextMenuRequest } from './render/office-engine';

/**
 * The right-click / long-press CONTEXT MENU over the world (ТЗ). The engine raises a {@link ContextMenuRequest}
 * for whatever is under the cursor — a placed FURNITURE piece or an interactive OBJECT — and this renders the
 * matching actions at that screen point:
 *
 *   • Object   → "Interact" (the same action as the E key).
 *   • Furniture (yours / you're the host) → "Pick up & move", "Rotate", "Remove".
 *   • Furniture (someone else's) → a gentle "only the owner or host can change this" note (no actions).
 *
 * All actions are server-authoritative (the realtime room re-checks the right + position); this menu only
 * surfaces them. Closes on action, click-away (backdrop), or Esc. Portaled to <body> so it's never clipped.
 */
export interface FurnitureContextMenuProps {
  request: ContextMenuRequest | null;
  onClose: () => void;
  /** Interact with an object OR an interactive placed piece (same as pressing E on it). */
  onInteract: (objectId: string) => void;
  /** Sit on (or, if already seated, stand up from) a placed sit-able piece. */
  onSit: (furnitureId: string) => void;
  /** "Take": pick the piece up and carry it to a new spot (a no-coin move). */
  onTake: (furnitureId: string) => void;
  /** Rotate the piece 90°. */
  onRotate: (furnitureId: string) => void;
  /** Remove the piece from the room. */
  onRemove: (furnitureId: string) => void;
}

export function FurnitureContextMenu({
  request,
  onClose,
  onInteract,
  onSit,
  onTake,
  onRotate,
  onRemove,
}: FurnitureContextMenuProps): JSX.Element | null {
  const menuRef = useRef<HTMLDivElement>(null);

  // Esc closes; focus the menu so it's reachable + announced.
  useEffect(() => {
    if (!request) return;
    menuRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, onClose]);

  if (!request) return null;

  const { screenX, screenY, target } = request;
  // Clamp so the menu never runs off the right/bottom edge (rough width/height estimate).
  const left = Math.min(screenX, window.innerWidth - 200);
  const top = Math.min(screenY, window.innerHeight - 180);

  const run = (fn: () => void): void => {
    fn();
    onClose();
  };

  return createPortal(
    <>
      {/* Click-away backdrop (also swallows the next contextmenu so a second right-click re-targets). */}
      <div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        role="menu"
        tabIndex={-1}
        aria-label={target.type === 'furniture' ? target.name : target.label}
        style={{ left, top }}
        className="fixed z-[61] min-w-[11rem] overflow-hidden rounded-xl border border-white/10 bg-slate-950/95 p-1 shadow-2xl outline-none backdrop-blur-xl"
      >
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-white/90">
          <span aria-hidden="true">{target.type === 'object' ? target.icon : '🪑'}</span>
          <span className="truncate">{target.type === 'furniture' ? target.name : target.label}</span>
        </div>
        <div className="my-1 h-px bg-white/10" />

        {target.type === 'object' ? (
          <MenuItem icon="👆" label="Interact" onClick={() => run(() => onInteract(target.objectId))} />
        ) : (
          <>
            {/* Use-actions — available to ANYONE in the room (like the static objects). */}
            {target.interactive && (
              <MenuItem icon="👆" label="Interact" onClick={() => run(() => onInteract(target.id))} />
            )}
            {target.sittable &&
              (target.occupied ? (
                <MenuItem icon="🪑" label="Taken" disabled onClick={() => {}} />
              ) : (
                <MenuItem
                  icon={target.seatedHere ? '🧍' : '🪑'}
                  label={target.seatedHere ? 'Stand up' : 'Sit here'}
                  onClick={() => run(() => onSit(target.id))}
                />
              ))}
            {/* Edit-actions — only the placer or the room host. */}
            {target.canModify ? (
              <>
                {(target.interactive || target.sittable) && <div className="my-1 h-px bg-white/10" />}
                <MenuItem icon="🖐️" label="Pick up & move" onClick={() => run(() => onTake(target.id))} />
                <MenuItem icon="🔄" label="Rotate" onClick={() => run(() => onRotate(target.id))} />
                <MenuItem icon="🗑️" label="Remove" danger onClick={() => run(() => onRemove(target.id))} />
              </>
            ) : (
              !target.interactive &&
              !target.sittable && (
                <p className="px-3 py-2 text-[11px] leading-snug text-white/45">
                  Only the owner or the room host can change this piece.
                </p>
              )
            )}
          </>
        )}
      </div>
    </>,
    document.body,
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger = false,
  disabled = false,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/50 disabled:cursor-not-allowed disabled:text-white/35 disabled:hover:bg-transparent ${
        danger ? 'text-red-200 hover:bg-red-500/20' : 'text-white/90 hover:bg-white/10'
      }`}
    >
      <span aria-hidden="true" className="w-5 text-center">
        {icon}
      </span>
      {label}
    </button>
  );
}
